
import { useAuthContext } from 'contexts';
import FHIRFetcher from 'data/FHIRFetcher';
import type { RevalidateTrigger } from 'data/FHIRFetcher';
import HooksPoster from 'data/HooksPoster';
import type { Task, Flag, Provenance, BundleEntry } from 'fhir/r4';
import { FHIRUtils, ProvenanceBuilder, FlagBuilder } from 'fhir-sdoh';
import moment from 'moment';
import { useCallback } from 'react';
import * as Helpers from 'utils/Helpers';

import type { DefaultOptionType, FlatPractitionerSelectOption } from '../../types';

import {
  applyConsultMemberEventDueDate,
  getOrganizationReference,
  getTaskPatientId,
  isAutomatedTask,
  isConsultationTask,
  isFfsMntConsultationTask,
  isFoodDeliveryTask,
  isInterventionOrEnrollmentTask,
  shouldUpdateExecutionPeriod,
} from './utils';

type TaskAssignmentHookProps = {
  flatPractitioners: FlatPractitionerSelectOption[];
  foodVendors: DefaultOptionType[];
  orgCode: string;
  tasksMutate: RevalidateTrigger;
};

export const useTaskAssignment = ({ flatPractitioners, foodVendors, orgCode, tasksMutate }: TaskAssignmentHookProps) => {
  const authInfo = useAuthContext();

  const getPatientOrganization = useCallback(
    async (taskDetails: Task): Promise<string> => {
      const patientId = getTaskPatientId(taskDetails);
      // Get organization from task details, if org doesn't exist, fetch patient managing organization
      let org = taskDetails?.basedOn
        ?.find((obj) => obj?.reference?.startsWith('Organization'))
        ?.reference?.split('/')?.[1];
      if (!org) {
        // A FHIR *read* (`/Patient/{id}`), not a search, so this resolves to the
        // Patient resource rather than a Bundle — hence the explicit generic,
        // since `FHIRFetcher` defaults its own to `Bundle`.
        await FHIRFetcher<fhir4.Patient>(`/Patient/${patientId}`, authInfo.token).then(
          (patient) => {
            org = patient?.managingOrganization?.reference?.split('/')?.[1];
          },
          (reason) => {
            console.error('Error fetching patient info: ', reason);
          }
        );
      }

      // SUSPECTED DEFECT — declared `Promise<string>`, but `org` is genuinely
      // `string | undefined` on three paths: the task carries no `Organization`
      // in `basedOn`, the `/Patient/{id}` read rejects (the error is logged and
      // swallowed above), or the patient carries no `managingOrganization`
      // (`0..1` in FHIR). Every caller feeds the result straight into
      // `getOrganizationReference`, which calls `.toUpperCase()` on it — so an
      // absent org throws there rather than here.
      //
      // Reported, not fixed: the honest signature is `Promise<string |
      // undefined>`, but widening it pushes the decision into
      // `getOrganizationReference`, and defaulting to `''` would silently write
      // a provenance record with an empty organization reference against a
      // patient's care plan. The assertion preserves today's behaviour exactly.
      return org!;
    },
    [authInfo.token]
  );

  const assignTask = useCallback(
    async (assignee: string, taskDetails: Task): Promise<BundleEntry[]> => {
      taskDetails.owner = {
        reference: 'Practitioner/' + assignee.toString(),
        // `assignee` is the id the user picked from `flatPractitioners`, so the
        // filter always matches. Asserted rather than defaulted: a miss would
        // mean the picker offered a practitioner that is not in its own list,
        // and today that throws rather than silently assigning a task with a
        // blank owner display.
        display: flatPractitioners.filter((practitioner) => {
          return practitioner.id === assignee;
        })[0]!.fullName,
      };

      let currentOrgCode = orgCode;
      if (currentOrgCode === 'All') {
        currentOrgCode = await getPatientOrganization(taskDetails);
      }

      const orgRef = getOrganizationReference(currentOrgCode);
      const patientId = getTaskPatientId(taskDetails);

      taskDetails = FHIRUtils.addUpdatedBy(taskDetails, authInfo.practitionerName);

      // create Provenance
      const provenance: Provenance = ProvenanceBuilder.build(
        `Task/${taskDetails.id!}`,
        { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName },
        patientId,
        'UPDATE',
        `Assignee has been changed to ` +
          taskDetails.owner?.display?.toString() +
          ` on ${taskDetails?.code?.coding?.[0]?.code} Task `,
        orgRef
      );

      //do not create flag if a task is food delivery or consultation task
      // The literal array is widened to `(string | undefined)[]` rather than
      // coercing the argument: `Task.code.coding[0].code` is genuinely optional
      // in FHIR, and `includes(undefined)` already evaluates to `false` here —
      // which is the branch an unclassifiable task takes today.
      if (
        !(['consultation', 'food-delivery', 'other-goods-delivery'] as (string | undefined)[]).includes(
          taskDetails?.code?.coding?.[0]?.code
        )
      ) {
        const flagDetails: Flag = FlagBuilder.build(
          patientId,
          authInfo.practitionerId,
          { code: 'admin', display: 'administrative' },
          { code: 'ALERT', display: 'Alert' },
          taskDetails.code?.coding?.[0]?.code?.toString().toUpperCase() +
            ' related task assigned to you by ' +
            authInfo.practitionerName,
          authInfo.practitionerName,
          currentOrgCode,
          taskDetails.owner?.reference?.split('/')[1],
          'active',
          taskDetails.id
        );
        // `buildResourceEntry` is declared `any` in `fhir-sdoh/common/fhirutils`. Cast
        // at each call rather than widening that return.
        return [
          FHIRUtils.buildResourceEntry(taskDetails, false),
          FHIRUtils.buildResourceEntry(flagDetails, false),
          FHIRUtils.buildResourceEntry(provenance, false),
        ] as BundleEntry[];
      } else {
        return [
        FHIRUtils.buildResourceEntry(taskDetails, false),
        FHIRUtils.buildResourceEntry(provenance, false),
      ] as BundleEntry[];
      }
    },
    [authInfo, flatPractitioners, orgCode, getPatientOrganization]
  );

  /*
   * SUSPECTED DEFECT — `Task.executionPeriod` is `0..1` in FHIR R4 and this
   * hook writes into `executionPeriod.start` / `.end` without establishing the
   * object exists. Sites: `handleChangeDate` (below) and `changeStatus`
   * (`executionPeriod.end` on cancel, `.start`/`.end` on food-delivery
   * completion). A Task persisted without an `executionPeriod` makes every one
   * of those throw "Cannot set properties of undefined", inside the save path
   * for a care-plan task.
   *
   * Two things make this look like an oversight rather than an invariant:
   *
   *  1. The *reads* in the very same expressions already treat it as optional —
   *     `taskDetails.executionPeriod?.start` at the `shouldUpdateExecutionPeriod`
   *     call and in the provenance `message`. The reads guard; the writes do
   *     not.
   *  2. The sibling helper `applyConsultMemberEventDueDate` in `./utils`
   *     assigns the whole object (`task.executionPeriod = { start, end }`),
   *     which is safe when absent. The branches here assign into a
   *     sub-property, which is not. Same file, same shape, different safety.
   *
   * Reported, not fixed. The correct repair is almost certainly to assign the
   * whole `executionPeriod` object as `applyConsultMemberEventDueDate` does,
   * but that decides what an absent period *means* for a task whose due date
   * staff are editing, and it silently changes what gets PUT back to the FHIR
   * server. The `!` below keeps today's throw exactly.
   */
  const handleChangeDate = useCallback(
    async (dueDate: string, taskDetails: Task): Promise<BundleEntry[]> => {
      let currentOrgCode = orgCode;
      if (currentOrgCode === 'All') {
        currentOrgCode = await getPatientOrganization(taskDetails);
      }

      const orgRef = getOrganizationReference(currentOrgCode);
      const patientId = getTaskPatientId(taskDetails);

      if (!isAutomatedTask(taskDetails) && !isFoodDeliveryTask(taskDetails)) {
        if (
          shouldUpdateExecutionPeriod(
            dueDate,
            taskDetails.meta?.lastUpdated || '',
            taskDetails.executionPeriod?.start || '',
            taskDetails.executionPeriod?.end || ''
          )
        ) {
          // if the selected new due date is smaller than last updated date and both executionPeriod, then we will update the executionPeriod to selected new due date
          //
          // SUSPECTED DEFECT (cluster; see the block comment above this hook's
          // `changeDueDate`) — writes into `executionPeriod.start/.end` on a
          // Task whose `executionPeriod` FHIR permits to be absent.
          taskDetails.executionPeriod!.start = moment(dueDate).format('YYYY-MM-DD');
          taskDetails.executionPeriod!.end = moment(dueDate).format('YYYY-MM-DD');
        } else if (
          moment(taskDetails.executionPeriod?.start).format('YYYY-MM-DD') > moment(dueDate).format('YYYY-MM-DD')
        ) {
          // if both start date and last updated date is greater than the current new due date, then we will set start date as today. Otherwise, we will mark last updated date as start date
          if (moment(taskDetails.meta?.lastUpdated).format('YYYY-MM-DD') > moment(dueDate).format('YYYY-MM-DD')) {
            taskDetails.executionPeriod!.start = moment().format('YYYY-MM-DD');
          } else {
            taskDetails.executionPeriod!.start = moment(taskDetails.meta?.lastUpdated).format('YYYY-MM-DD');
            taskDetails.executionPeriod!.end = moment(dueDate).format('YYYY-MM-DD');
          }
        } else {
          taskDetails.executionPeriod!.start = moment(taskDetails.executionPeriod!.start).format('YYYY-MM-DD');
          taskDetails.executionPeriod!.end = moment(dueDate).format('YYYY-MM-DD');
        }
      }
      // set both execution period the same date for automated tasks and food delivery tasks
      else if (isAutomatedTask(taskDetails) || isFoodDeliveryTask(taskDetails)) {
        taskDetails.executionPeriod = {
          end: moment(dueDate).format('YYYY-MM-DD'),
          start: moment(dueDate).format('YYYY-MM-DD'),
        };
      }

      taskDetails = FHIRUtils.addUpdatedBy(taskDetails, authInfo.practitionerName);

      const message = `Task due date has been changed to ${taskDetails.executionPeriod?.end} on ${taskDetails?.code?.coding?.[0]?.code} Task`;

      // create Provenance
      const provenance: Provenance = ProvenanceBuilder.build(
        `Task/${taskDetails.id!}`,
        { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName },
        patientId,
        'UPDATE',
        message,
        orgRef
      );

      return [
        FHIRUtils.buildResourceEntry(taskDetails, false),
        FHIRUtils.buildResourceEntry(provenance, false),
      ] as BundleEntry[];
    },
    [authInfo, orgCode, getPatientOrganization]
  );

  const changeStatus = useCallback(
    async (newStatus: string, taskDetails: Task, memberEventDate?: string): Promise<BundleEntry[]> => {
      const oldTaskStatus = taskDetails.status;
      const taskHelper = new Helpers.TaskHelper(taskDetails);

      taskDetails.status = newStatus as Task['status'];

      if (taskDetails?.statusReason) {
        taskDetails.statusReason = {
          text: '',
        };
      }

      const taskCode = taskHelper.getCode();
      let currentOrgCode = orgCode;

      if (currentOrgCode === 'All') {
        currentOrgCode = await getPatientOrganization(taskDetails);
      }

      const orgRef = getOrganizationReference(currentOrgCode);
      const patientId = getTaskPatientId(taskDetails);

      // FFS-MNT consult failed/cancelled: set due date in the same write when a member event date is provided
      // (and the bulk form did not already apply a due-date change).
      if (
        isFfsMntConsultationTask(taskDetails) &&
        ['cancelled', 'failed'].includes(taskDetails.status) &&
        memberEventDate
      ) {
        applyConsultMemberEventDueDate(taskDetails, memberEventDate);
      } else if (
        isInterventionOrEnrollmentTask(taskCode, taskDetails?.reasonCode?.text || '') &&
        taskDetails.status === 'cancelled'
      ) {
        // if intervention or enrollment task is marked as cancelled, update due date to today
        // SUSPECTED DEFECT — see the `executionPeriod` cluster note above.
        taskDetails.executionPeriod!.end = moment().format('YYYY-MM-DD');
      } else if (
        (['food-delivery', 'other-goods-delivery'] as (string | undefined)[]).includes(
          taskDetails?.code?.coding?.[0]?.code
        ) &&
        taskDetails.status === 'completed'
      ) {
        //update the food delivery task due date to today if status is marked as completed so we use that to query and create phone only hanlder in task-betty
        // SUSPECTED DEFECT — see the `executionPeriod` cluster note above.
        taskDetails.executionPeriod!.start = moment().format('YYYY-MM-DD');
        taskDetails.executionPeriod!.end = moment().format('YYYY-MM-DD');
      }

      taskDetails = FHIRUtils.addUpdatedBy(taskDetails, authInfo.practitionerName);

      // if intervention or enrollment task is marked as cancelled, update patient status
      if (
        isInterventionOrEnrollmentTask(taskCode, taskDetails?.reasonCode?.text || '') &&
        taskDetails.status === 'cancelled'
      ) {
        // Update all 'requested', 'in-progress',and 'on-hold' tasks to 'cancelled' when intervention task is 'cancelled'
        const updateTasksAndPatientStatusPayload = {
          patientId: patientId,
          status: 'INACTIVE',
          reason: 'DISENROLL',
          taskId: taskDetails.id,
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
        HooksPoster(`/hooks/patient/status`, authInfo.token, updateTasksAndPatientStatusPayload).finally(() => {
          setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
            tasksMutate();
          }, 3000);
        });
      } else if (
        isInterventionOrEnrollmentTask(taskCode, taskDetails?.reasonCode?.text || '') &&
        taskDetails.status === 'in-progress' &&
        oldTaskStatus === 'cancelled'
      ) {
        // Update all not due 'cancelled' tasks to 'requested' when intervention task status is updated from 'cancelled' to 'in-progress'
        const updateTasksAndPatientStatusPayload = {
          patientId: patientId,
          status: 'ACTIVE',
          reason: 'REENROLL',
          taskId: taskDetails.id,
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
        HooksPoster(`/hooks/patient/status`, authInfo.token, updateTasksAndPatientStatusPayload).finally(() => {
          setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
            tasksMutate();
          }, 3000);
        });
      } else {
        setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
          tasksMutate();
        }, 3000);
      }

      const statusLabel = taskDetails.status === 'received' ? 'outreach' : taskDetails.status?.toString();
      const provenanceActivity =
        isConsultationTask(taskDetails) && memberEventDate
          ? `Status has been changed to ${statusLabel} and due date set to ${memberEventDate} on ${taskDetails?.code?.coding?.[0]?.code} Task `
          : `Status has been changed to ${statusLabel} on ${taskDetails?.code?.coding?.[0]?.code} Task `;

      // create Provenance
      const provenance: Provenance = ProvenanceBuilder.build(
        `Task/${taskDetails.id!}`,
        { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName },
        patientId,
        'UPDATE',
        provenanceActivity,
        orgRef
      );

      return [
        FHIRUtils.buildResourceEntry(taskDetails, false),
        FHIRUtils.buildResourceEntry(provenance, false),
      ] as BundleEntry[];
    },
    [authInfo, orgCode, getPatientOrganization]
  );

  const changeVendor = useCallback(
    async (vendorValue: string, taskDetails: Task): Promise<BundleEntry[]> => {
      let currentOrgCode = orgCode;
      if (currentOrgCode === 'All') {
        currentOrgCode = await getPatientOrganization(taskDetails);
      }

      const orgRef = getOrganizationReference(currentOrgCode);
      const patientId = getTaskPatientId(taskDetails);

      const identifiersList = taskDetails?.identifier ? [...taskDetails.identifier] : [];

      const vendorName = foodVendors?.find((v) => v.value === vendorValue)?.text || vendorValue;

      taskDetails.restriction = {
        recipient: [
          {
            reference: 'Organization/' + vendorValue,
            display: vendorName,
          },
        ],
      };

      const vendorIdentifierIndex = identifiersList.findIndex((identifier) => {
        return identifier.system?.toString().includes('vendor-codes');
      });

      if (vendorIdentifierIndex === -1) {
        identifiersList.push({
          system: 'https://projectwell.io/fhir/identifiers/vendor-codes',
          value: vendorValue,
        });
      } else {
        // Reached only when `findIndex` returned something other than -1, so
        // the element exists. `noUncheckedIndexedAccess` cannot carry that
        // across the `findIndex` boundary; the assertion states it.
        identifiersList[vendorIdentifierIndex]!['system'] = 'https://projectwell.io/fhir/identifiers/vendor-codes';
        identifiersList[vendorIdentifierIndex]!['value'] = vendorValue;
      }

      taskDetails.identifier = identifiersList;

      taskDetails = FHIRUtils.addUpdatedBy(taskDetails, authInfo.practitionerName);

      // create Provenance
      const provenance: Provenance = ProvenanceBuilder.build(
        `Task/${taskDetails.id!}`,
        { reference: 'Practitioner/' + authInfo.practitionerId, display: authInfo.practitionerName },
        patientId,
        'UPDATE',
        `Vendor has been changed to ${vendorName} on ${taskDetails?.code?.coding?.[0]?.code} Task `,
        orgRef
      );

      return [
        FHIRUtils.buildResourceEntry(taskDetails, false),
        FHIRUtils.buildResourceEntry(provenance, false),
      ] as BundleEntry[];
    },
    [authInfo, orgCode, getPatientOrganization, foodVendors]
  );

  return {
    assignTask,
    changeStatus,
    handleChangeDate,
    changeVendor,
  };
};
